import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["timestamp", "range", "references"];
  static outlets = ["typeahead-component"];

  connect() {
    this.currentEditingReference = null;
    this.counter = 0;

    // attach an event listener to the audio metadata field to know when the audio is loaded
    const audioMeta = document.querySelector("#episode_podcast_audio_meta");
    if (audioMeta) {
      audioMeta.addEventListener("metadata-updated", this.audioMetaDidUpdate.bind(this));
    }

    // used when editing an existing reference. The audio element is going to emit this event
    // every time the current time changes
    document.addEventListener("timestamp-component:addReference", (event) => {
      this.addReference(null, event.detail.label, event.detail.questionId);
    });

    // used to remove the highlight from the reference being edited
    document.body.addEventListener("click", (event) => {
      this.editReference(event);
    });

    // used when editing an existing reference. We'll update the timestamp and the formatted timestamp
    // every time the current time changes (if not actually playing)
    this.timestampTarget.addEventListener("timeupdate", () => {
      if (this.currentEditingReference && this.timestampTarget.paused) {
        const newTimestamp = this.timestampTarget.currentTime;
        this.currentEditingReference.querySelector(".episode-timestamp-timestamp").value = newTimestamp;
        this.currentEditingReference.querySelector(".episode-timestamp-formatted").innerHTML = this.formatTimestamp(newTimestamp);
        this.sortReferences();
      }
    });

    // stop editing a reference if we're editing one and the user clicks play
    this.timestampTarget.addEventListener("play", (event) => {
      this.editReference(event);
    })

    // dispatched when the timestamp component is ready
    document.dispatchEvent(new CustomEvent("timestamp-component:ready"));
  }

  disconnect() {
    // remove listeners we've added
    document.removeEventListener("timestamp-component:addReference");
    document.body.removeEventListener("click");
  }

  destination() {
    return this.referencesTarget;
  }

  audioMetaDidUpdate(event) {
    if (event.detail.url) {
      this.timestampTarget.setAttribute("src", event.detail.url);
      this.element.classList.remove("hidden");
    }
  }

  formatTimestamp(timestamp) {
    let minutes = Math.floor(timestamp / 60);
    let seconds = Math.floor(timestamp % 60);
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    seconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}:${seconds}`;
  }

  removeReference(event) {
    event.stopPropagation();
    const episodeNode = event.target.closest(".episode-timestamp")
    const id = episodeNode.querySelector(".episode-timestamp-id");
    if (id.value == "") {
      episodeNode.outerHTML = "";
    } else {
      const destroy = episodeNode.querySelector(".episode-timestamp-destroy");
      destroy.value = "1";
      episodeNode.style.display = "none";
    }
  }

  editReference(event) {
    event.stopPropagation();
    // find the root element of the reference being edited
    const episodeNode = event.target.closest(".episode-timestamp");
    if (!episodeNode && !this.currentEditingReference) return;

    // the user is clicking on the same reference, so we should stop editing
    if (episodeNode == this.currentEditingReference) {
      // stop editing
      episodeNode.classList.remove("bg-gray-200");
      this.currentEditingReference = null;
      return;

    // the user clicked on a reference before and now is clicking somewhere else
    } else if (this.currentEditingReference != null) {
      // stop editing the previous reference
      this.currentEditingReference.classList.remove("bg-gray-200");
      // this means the user did not click on a reference, so we should stop editing as well
      if (!episodeNode) {
        this.currentEditingReference = null;
        return;
      }
    }

    // start editing the new reference
    episodeNode.classList.add("bg-gray-200");
    this.currentEditingReference = episodeNode;
  }

  sortReferences() {
    const references = this.destination().querySelectorAll(".episode-timestamp");
    const sortedReferences = Array.from(references).sort((a, b) => {
      const aTimestamp = a.querySelector(".episode-timestamp-timestamp").value;
      const bTimestamp = b.querySelector(".episode-timestamp-timestamp").value;
      return aTimestamp - bTimestamp;
    });
    this.destination().innerHTML = "";
    sortedReferences.forEach((reference) => {
      this.destination().appendChild(reference);
    });
  }

  createReference(label = null, id = null) {
    const timestamp = this.timestampTarget.currentTime;
    const formattedTimestamp = this.formatTimestamp(timestamp);
    const items = this.typeaheadComponentOutlet.selectedItems();
    const text = label || items[0]?.label;
    const questionId = Number(id || items[0]?.value);

    if (!text) return;

    return `
      <div class="episode-timestamp w-full flex flex-row p-1 hover:bg-gray-200 cursor-pointer" data-action="click->episode-timestamp-component#editReference">
        <div class="w-4/5">
          <input class="episode-timestamp-id" type="hidden" name="episode[episode_timestamps_attributes][][id]" value="">
          <input class="episode-timestamp-timestamp" type="hidden" name="episode[episode_timestamps_attributes][][timestamp]" value="${timestamp}">
          <input type="hidden" name="episode[episode_timestamps_attributes][][title]" value="${text}">
          <input class="episode-timestamp-destroy" type="hidden" name="episode[episode_timestamps_attributes][][_destroy]" value="0">
          <div class="text-left">(<span class="episode-timestamp-formatted">${formattedTimestamp}</span>) ${text}</div>
        </div>
        <div class="w-1/5 flex items-center justify-center">
          <a href="#"
            data-action="click->episode-timestamp-component#removeReference"
            class="no-underline">x</a>
        </div>
        ${questionId > 0 ? this.createQuestionId(questionId) : ''}
      </div>
    `;
  }

  createQuestionId(questionId) {
    if (!questionId) return "";
    return `<input type="hidden" name="episode[episode_timestamps_attributes][][question_id]" value="${questionId}">`;
  }

  addReference(event, label = null, questionId = null) {
    let reference = this.createReference(label, questionId);
    if (!reference) return;
    this.destination().innerHTML += reference;
    this.counter += 1;
    this.sortReferences();
    this.typeaheadComponentOutlet.clearItems();
  }
}
